import { createIcons, Download, X } from "lucide";

import { trackEvent } from "./analytics.ts";

interface InstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

export function setupInstall(): void {
  const controls = document.querySelector<HTMLElement>("[data-install-controls]");
  if (!controls) return;
  const button = controls.querySelector<HTMLButtonElement>("[data-install]")!;
  const dialog = controls.querySelector<HTMLDialogElement>("[data-install-dialog]")!;
  const help = controls.querySelector<HTMLElement>("[data-install-help]")!;
  const displayMode = window.matchMedia("(display-mode: standalone), (display-mode: fullscreen), (display-mode: minimal-ui)");
  const appleDevice = /iPad|iPhone|iPod/.test(navigator.userAgent)
    || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
  const androidDevice = /Android/i.test(navigator.userAgent);
  let pendingPrompt: InstallPromptEvent | undefined;
  let installed = false;
  let prompting = false;

  createIcons({ icons: { Download, X }, root: controls });

  function update(): void {
    const standalone = displayMode.matches || (navigator as Navigator & { standalone?: boolean }).standalone === true;
    controls!.hidden = installed || standalone || !(pendingPrompt || appleDevice || androidDevice || dialog.open);
    button.disabled = prompting;
    if (controls!.hidden && dialog.open) dialog.close();
  }

  function showHelp(): void {
    help.textContent = appleDevice
      ? "Tap your browser's Share button, then Add to Home Screen. Leave Open as Web App on if shown, then tap Add. If this option is missing, open codelet.uk in Safari."
      : androidDevice
        ? "Open your browser's menu and choose Install app or Add to Home screen. If neither is shown, open codelet.uk in Chrome."
        : "Use the install icon in your browser's address bar or choose Install app from its menu.";
    dialog.showModal();
  }

  window.addEventListener("beforeinstallprompt", (event) => {
    event.preventDefault();
    pendingPrompt = event as InstallPromptEvent;
    update();
  });
  window.addEventListener("appinstalled", () => {
    trackEvent("app_installed");
    installed = true;
    pendingPrompt = undefined;
    update();
  });
  displayMode.addEventListener("change", update);
  window.addEventListener("pageshow", update);

  button.addEventListener("click", async () => {
    if (prompting) return;
    const request = pendingPrompt;
    trackEvent("install_clicked", { prompt: request ? "native" : "help", device: appleDevice ? "apple" : androidDevice ? "android" : "other" });
    if (!request) {
      showHelp();
      return;
    }
    pendingPrompt = undefined;
    prompting = true;
    button.disabled = true;
    try {
      await request.prompt();
      const choice = await request.userChoice;
      if (choice.outcome === "accepted") installed = true;
    } catch {
      showHelp();
    } finally {
      prompting = false;
      update();
    }
  });
  controls.querySelector("[data-close-install]")!.addEventListener("click", () => dialog.close());
  dialog.addEventListener("close", () => {
    update();
    if (!controls.hidden) button.focus({ preventScroll: true });
  });
  update();
}